import { getEnabledProductApps } from '~/shell/auth/useEnabledProducts'
import { getLastApp } from '~/shell/storage/lastApp'

import { appById } from './registry'

interface LandingApp {
  id: string
}

export interface RequestedApp {
  id: string
  recognized: boolean
}

export type EntitlementRedirectReason =
  | 'requested-unavailable'
  | 'last-viewed-unavailable'
  | 'no-enabled-apps'

export interface EntitlementRedirect {
  reason: EntitlementRedirectReason
  requestedApp?: string
  resolvedApp?: string
}

export type LandingAppResolution<AppType extends LandingApp> =
  | {
      app: AppType
      entitlementRedirect?: EntitlementRedirect
    }
  | {
      app: undefined
      entitlementRedirect: EntitlementRedirect
    }

/**
 * Resolves which app a session should enter from the apps it is entitled to reach.
 *
 * The requested and last-viewed values are preferences only: each is validated against the
 * entitled list before use. Registry order is preserved, so the first item is the deterministic
 * fallback when neither preference is available.
 *
 * Pure, and the one place this precedence is written down. `Bootstrap`, a bare customer path, an
 * unknown app segment, a `disabled` app, and an unpermitted one all reach it through
 * `AppResolutionRedirect`, so none of them can drift from the others (Atlas ADR-0015). Keeping it out
 * of a component is also what makes the whole input space reachable from unit tests, including the
 * case that matters most: a last-viewed app the session can no longer open must resolve to one it
 * can, never to the one named.
 */
export const resolveLandingApp = <AppType extends LandingApp>(
  entitledApps: AppType[],
  lastViewedAppId: string | null,
  requestedApp: RequestedApp | null,
): LandingAppResolution<AppType> => {
  if (entitledApps.length === 0) {
    return {
      app: undefined,
      entitlementRedirect: {
        reason: 'no-enabled-apps',
        ...(requestedApp ? { requestedApp: requestedApp.id } : {}),
      },
    }
  }

  const requested = entitledApps.find((app) => app.id === requestedApp?.id)
  if (requested) {
    return { app: requested }
  }

  const lastViewed = entitledApps.find((app) => app.id === lastViewedAppId)
  const app = lastViewed ?? entitledApps[0]

  if (requestedApp?.recognized) {
    return {
      app,
      entitlementRedirect: {
        reason: 'requested-unavailable',
        requestedApp: requestedApp.id,
        resolvedApp: app.id,
      },
    }
  }

  if (lastViewedAppId !== null && !lastViewed) {
    return {
      app,
      entitlementRedirect: {
        reason: 'last-viewed-unavailable',
        resolvedApp: app.id,
      },
    }
  }

  return { app }
}

/**
 * Resolves a requested app against the current session's entitlements and stored preference.
 *
 * Keeps session and registry reads outside the pure resolver while giving callers an interface that
 * only asks for the app named by the URL.
 */
export const resolveSessionLandingApp = (requestedAppId?: string) =>
  resolveLandingApp(
    getEnabledProductApps(),
    getLastApp(),
    requestedAppId === undefined
      ? null
      : {
          id: requestedAppId,
          recognized: appById(requestedAppId) !== undefined,
        },
  )
